import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote, Users, X, Camera, CameraOff, Printer, CheckCircle } from 'lucide-react';
import { useReceiptPrinter } from '../../components/ReceiptPrinter';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

export default function HesapPOSPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const searchRef = useRef(null);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState('');
  const [note, setNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('nakit');
  const [isVeresiye, setIsVeresiye] = useState(false);
  const [paidAmount, setPaidAmount] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomers, setShowCustomers] = useState(false);
  const [accountLabel, setAccountLabel] = useState(''); // Seçili müşteri için alt-hesap
  const [customerAccounts, setCustomerAccounts] = useState([]); // Seçili müşterinin mevcut alt-hesapları
  const [processing, setProcessing] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [showNewProductDialog, setShowNewProductDialog] = useState(false);
  const [newProductBarcode, setNewProductBarcode] = useState('');
  const [lastSale, setLastSale] = useState(null);
  const { printReceipt } = useReceiptPrinter();

  useEffect(() => {
    loadCustomers();
    searchRef.current?.focus();
  }, []);

  const loadCustomers = async () => {
    const res = await fetch(`${API}/customers`, { headers });
    if (res.ok) setCustomers(await res.json());
  };

  // Seçili müşteri değişince alt-hesap listesini yükle
  useEffect(() => {
    if (!selectedCustomer) {
      setCustomerAccounts([]);
      setAccountLabel('');
      return;
    }
    fetch(`${API}/customers/${selectedCustomer.id}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && Array.isArray(d.accounts)) setCustomerAccounts(d.accounts);
        else setCustomerAccounts([]);
      })
      .catch(() => setCustomerAccounts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer]);

  useEffect(() => {
    if (search.length < 1) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      const res = await fetch(`${API}/products/search?q=${encodeURIComponent(search)}`, { headers });
      if (res.ok) setSearchResults(await res.json());
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  const handleSearchKeyDown = async (e) => {
    if (e.key === 'Enter' && search.trim()) {
      e.preventDefault();
      try {
        const res = await fetch(`${API}/products/search?q=${encodeURIComponent(search.trim())}`, { headers });
        if (res.ok) {
          const results = await res.json();
          if (results.length === 1) {
            addToCart(results[0]);
          } else if (results.length > 1) {
            const exactMatch = results.find(p => p.barcode === search.trim());
            if (exactMatch) addToCart(exactMatch);
            else setSearchResults(results);
          } else {
            setNewProductBarcode(search.trim());
            setShowNewProductDialog(true);
          }
        }
      } catch { /* ignore */ }
    }
  };

  // Kamera barkod okuyucu
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const audioCtxRef = useRef(null);
  const [scanFlash, setScanFlash] = useState(null); // {name, ts}

  // Bip sesi - Web Audio API ile generate
  const playBeep = () => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 1500;  // 1.5 kHz - market barkod tarayıcısı tonu
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.13);
    } catch {}
  };

  const showScanFlash = (name) => {
    setScanFlash({ name, ts: Date.now() });
    setTimeout(() => setScanFlash(null), 1300);
  };

  const startCamera = useCallback(async () => {
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      if (html5QrCodeRef.current) {
        try { await html5QrCodeRef.current.stop(); } catch {}
      }
      
      setCameraActive(true);
      await new Promise(r => setTimeout(r, 300));
      
      const scanner = new Html5Qrcode("barcode-reader");
      html5QrCodeRef.current = scanner;
      
      const scanConfig = { fps: 10, qrbox: { width: 250, height: 100 } };
      const COOLDOWN_MS = 1800;  // aynı/yeni barkod sayım arasında min süre

      const onSuccess = async (decodedText) => {
        const now = Date.now();
        // Cooldown — aynı veya farklı kod fark etmez, 1.8sn süre boyunca yeni okuma yok
        if (now - lastScanRef.current.at < COOLDOWN_MS) {
          return;
        }
        lastScanRef.current = { code: decodedText, at: now };

        try {
          const res = await fetch(`${API}/products/search?q=${encodeURIComponent(decodedText)}`, { headers });
          if (res.ok) {
            const results = await res.json();
            const exact = results.find(p => p.barcode === decodedText);
            const product = exact || (results.length > 0 ? results[0] : null);
            if (product) {
              addToCart(product);
              playBeep();
              showScanFlash(product.name);
              toast.success(`${product.name} sepete eklendi`);
            } else {
              setNewProductBarcode(decodedText);
              setShowNewProductDialog(true);
            }
          }
        } catch {}
      };
      
      // S23 Ultra gibi çoklu kameralı cihazlar için geniş fallback zinciri
      let started = false;
      // 1. Genel arka kamera
      try {
        await scanner.start({ facingMode: "environment" }, scanConfig, onSuccess);
        started = true;
      } catch {}
      // 2. Exact environment
      if (!started) {
        try {
          await scanner.start({ facingMode: { exact: "environment" } }, scanConfig, onSuccess);
          started = true;
        } catch {}
      }
      // 3. Kamera listesinden arka kamerayı bul
      if (!started) {
        try {
          const devices = await Html5Qrcode.getCameras();
          const backCam = devices.find(d => /back|rear|environment|arka/i.test(d.label));
          const camId = backCam ? backCam.id : (devices.length > 0 ? devices[0].id : null);
          if (camId) {
            await scanner.start({ deviceId: camId }, scanConfig, onSuccess);
            started = true;
          }
        } catch {}
      }
      if (!started) throw new Error('Hicbir kamera baslatilamadi');
    } catch (err) {
      setCameraActive(false);
      toast.error('Kamera acilamadi: ' + (err.message || ''));
    }
  }, []);

  const stopCamera = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.stop(); } catch {}
      html5QrCodeRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => { return () => { stopCamera(); }; }, [stopCamera]);

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(c => c.product_id === product.id);
      if (existing) {
        return prev.map(c => c.product_id === product.id
          ? { ...c, quantity: c.quantity + 1, total: (c.quantity + 1) * c.unit_price }
          : c
        );
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.sell_price || product.sale_price || 0,
        total: product.sell_price || product.sale_price || 0,
        stock: product.stock
      }];
    });
    setSearch('');
    setSearchResults([]);
    searchRef.current?.focus();
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(c => {
      if (c.product_id !== productId) return c;
      const newQty = Math.max(1, c.quantity + delta);
      return { ...c, quantity: newQty, total: newQty * c.unit_price };
    }));
  };

  const updatePrice = (productId, newPrice) => {
    setCart(cart.map(c =>
      c.product_id === productId
        ? { ...c, unit_price: parseFloat(newPrice) || 0, total: c.quantity * (parseFloat(newPrice) || 0) }
        : c
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(c => c.product_id !== productId));
  };

  const subtotal = cart.reduce((sum, c) => sum + c.total, 0);
  const discountAmount = parseFloat(discount) || 0;
  const total = subtotal - discountAmount;
  const paid = parseFloat(paidAmount) || 0;
  const remaining = Math.max(0, total - paid);

  const completeSale = async () => {
    if (cart.length === 0) { toast.error('Sepet boş'); return; }
    if (isVeresiye && !selectedCustomer) { toast.error('Veresiye için müşteri seçin'); return; }

    setProcessing(true);
    try {
      const res = await fetch(`${API}/sales`, {
        method: 'POST', headers,
        body: JSON.stringify({
          items: cart.map(({ stock, ...c }) => c),
          total_amount: subtotal,
          discount: discountAmount,
          payment_method: paymentMethod,
          customer_id: selectedCustomer?.id || '',
          customer_name: selectedCustomer?.full_name || '',
          is_veresiye: isVeresiye,
          paid_amount: isVeresiye ? paid : total,
          account_label: isVeresiye ? (accountLabel || '').trim() : '',
          note
        })
      });
      if (!res.ok) throw new Error();
      const saleData = await res.json();
      
      // Receipt data for printing
      setLastSale({
        id: saleData.id || Date.now().toString(),
        items: cart.map(c => ({ name: c.name, quantity: c.quantity, price: c.price })),
        total: total,
        table_no: '',
        note: note,
        created_at: new Date().toISOString(),
        created_by: selectedCustomer?.full_name || '',
        payment_method: paymentMethod,
        discount: discountAmount,
        is_veresiye: isVeresiye,
        paid_amount: isVeresiye ? paid : total,
      });
      
      let msg = `Satış tamamlandı! ${total.toLocaleString('tr-TR')} TL`;
      if (isVeresiye && remaining > 0) {
        msg += ` (Borç: ${remaining.toLocaleString('tr-TR')} TL)`;
      }
      toast.success(msg);
      setCart([]); setDiscount(''); setNote(''); setPaidAmount('');
      setSelectedCustomer(null); setIsVeresiye(false); setAccountLabel('');
      searchRef.current?.focus();
    } catch { toast.error('Satis kaydedilemedi'); }
    finally { setProcessing(false); }
  };

  const filteredCustomers = customers.filter(c =>
    c.full_name.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Yeni urun ekleme dialog
  const [newProdForm, setNewProdForm] = useState({ name: '', sale_price: '' });
  const saveNewProduct = async () => {
    if (!newProdForm.name.trim()) { toast.error('Ürün adı gerekli'); return; }
    try {
      const res = await fetch(`${API}/products`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name: newProdForm.name,
          barcode: newProductBarcode,
          sell_price: parseFloat(newProdForm.sale_price) || 0,
          purchase_price: 0, stock: 0, min_stock: 0, category: ''
        })
      });
      if (res.ok) {
        const prod = await res.json();
        addToCart(prod);
        toast.success(`"${prod.name}" eklendi ve sepete konuldu`);
        setShowNewProductDialog(false);
        setNewProdForm({ name: '', sale_price: '' });
      }
    } catch { toast.error('Ürün eklenemedi'); }
  };

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-120px)]" data-testid="hesap-pos-page">
      {/* Left: Search & Results */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            ref={searchRef}
            placeholder="Barkod okutun veya ürün adı yazın, Enter ile ekleyin..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="pl-10 pr-12 h-12 text-lg"
            data-testid="pos-search"
          />
          <button onClick={cameraActive ? stopCamera : startCamera}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition ${
              cameraActive ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
            }`} title={cameraActive ? 'Kamerayi kapat' : 'Barkod kamerasi ac'}>
            {cameraActive ? <CameraOff className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
          </button>
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border rounded-b-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              {searchResults.map(p => (
                <button key={p.id} onClick={() => addToCart(p)}
                  className="w-full flex items-center justify-between p-3 hover:bg-emerald-50 border-b text-left">
                  <div>
                    <span className="font-medium">{p.name}</span>
                    {p.barcode && <span className="text-xs text-slate-400 ml-2">{p.barcode}</span>}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-emerald-600">{(p.sell_price || p.sale_price || 0).toLocaleString('tr-TR')} TL</span>
                    <span className="text-xs text-slate-400 ml-2">Stok: {p.stock}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Camera scanner area */}
        <div className="relative">
          <div id="barcode-reader" style={cameraActive ? { width: '100%', minHeight: 250 } : { display: 'none' }} 
            className={cameraActive ? 'mb-3 rounded-lg overflow-hidden border-2 border-emerald-500' : ''} />
          {cameraActive && (
            <style>{`
              #barcode-reader video { width: 100% !important; height: auto !important; min-height: 200px !important; object-fit: cover !important; display: block !important; }
              #barcode-reader img[alt="Info icon"] { display: none !important; }
              #barcode-reader > div { border: none !important; }
            `}</style>
          )}
          {/* Scan başarı overlay - ürün eklendi göstergesi */}
          {cameraActive && scanFlash && (
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none px-4"
              data-testid="scan-success-flash">
              <div className="bg-emerald-500/95 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2 animate-pulse max-w-[90%]">
                <CheckCircle className="h-6 w-6 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-bold leading-tight">Ürün eklendi</div>
                  <div className="text-[11px] opacity-90 truncate">{scanFlash.name}</div>
                </div>
              </div>
            </div>
          )}
          {/* Cooldown göstergesi */}
          {cameraActive && (
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded pointer-events-none">
              Sonraki taramaya hazır olmak için ~1.8 sn bekleyin
            </div>
          )}
        </div>

        {/* Cart Table */}
        <Card className="flex-1 overflow-hidden flex flex-col min-h-[220px] max-h-[60vh] lg:max-h-none">
          <div className="bg-slate-50 border-b p-3 flex items-center gap-2">
            <ShoppingCart className="h-4 w-4 text-slate-600" />
            <span className="font-medium text-sm">Sepet ({cart.length} ürün)</span>
          </div>
          <div className="flex-1 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <div className="text-center">
                  <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Ürün arayarak sepete ekleyin</p>
                </div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="text-left p-2 pl-3">Ürün</th>
                    <th className="text-center p-2 w-28">Adet</th>
                    <th className="text-right p-2 w-24">Fiyat</th>
                    <th className="text-right p-2 w-24">Toplam</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map(item => (
                    <tr key={item.product_id} className="border-b">
                      <td className="p-2 pl-3 font-medium">{item.product_name}</td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => updateQuantity(item.product_id, -1)}
                            className="w-7 h-7 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <button onClick={() => updateQuantity(item.product_id, 1)}
                            className="w-7 h-7 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                      <td className="p-2">
                        <Input type="number" step="0.01" value={item.unit_price}
                          onChange={(e) => updatePrice(item.product_id, e.target.value)}
                          className="h-7 text-right text-sm w-20 ml-auto" />
                      </td>
                      <td className="p-2 text-right font-medium">{item.total.toLocaleString('tr-TR')} TL</td>
                      <td className="p-2">
                        <button onClick={() => removeFromCart(item.product_id)} className="text-red-400 hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>

      {/* Right: Payment Panel */}
      <div className="w-full lg:w-72 flex flex-col gap-3 flex-shrink-0">
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Ara Toplam</span>
              <span className="font-medium">{subtotal.toLocaleString('tr-TR')} TL</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500 flex-shrink-0">Indirim</span>
              <Input type="number" step="0.01" value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="h-8 text-right text-sm" placeholder="0" />
              <span className="text-sm">TL</span>
            </div>
            <div className="border-t pt-2 flex justify-between">
              <span className="font-bold text-lg">Toplam</span>
              <span className="font-bold text-lg text-emerald-600">{total.toLocaleString('tr-TR')} TL</span>
            </div>
          </CardContent>
        </Card>

        {/* Payment method */}
        <Card>
          <CardContent className="p-3 space-y-2">
            <p className="text-xs font-medium text-slate-500">Ödeme Yöntemi</p>
            <div className="grid grid-cols-3 gap-1">
              {[
                { id: 'nakit', icon: Banknote, label: 'Nakit' },
                { id: 'kart', icon: CreditCard, label: 'Kart' },
                { id: 'havale', icon: CreditCard, label: 'Havale' }
              ].map(pm => (
                <button key={pm.id} onClick={() => { setPaymentMethod(pm.id); setIsVeresiye(false); }}
                  className={`flex flex-col items-center p-2 rounded-lg border text-xs transition-colors ${
                    paymentMethod === pm.id && !isVeresiye ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'hover:bg-slate-50'
                  }`}>
                  <pm.icon className="h-4 w-4 mb-1" />{pm.label}
                </button>
              ))}
            </div>
            <button onClick={() => setIsVeresiye(!isVeresiye)}
              className={`w-full flex items-center justify-center gap-2 p-2 rounded-lg border text-xs transition-colors ${
                isVeresiye ? 'border-amber-500 bg-amber-50 text-amber-700' : 'hover:bg-slate-50'
              }`}>
              <Users className="h-4 w-4" />Veresiye
            </button>
          </CardContent>
        </Card>

        {/* Customer selection + paid amount for veresiye */}
        {isVeresiye && (
          <Card>
            <CardContent className="p-3 space-y-2">
              <p className="text-xs font-medium text-slate-500">Müşteri Seçin</p>
              {selectedCustomer ? (
                <div className="flex items-center justify-between bg-amber-50 p-2 rounded-lg">
                  <span className="text-sm font-medium">{selectedCustomer.full_name}</span>
                  <button onClick={() => setSelectedCustomer(null)}><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <>
                  <Input placeholder="Müşteri ara..." value={customerSearch}
                    onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomers(true); }}
                    className="h-8 text-sm" />
                  {showCustomers && customerSearch && (
                    <div className="max-h-32 overflow-y-auto border rounded">
                      {filteredCustomers.map(c => (
                        <button key={c.id} onClick={() => { setSelectedCustomer(c); setShowCustomers(false); setCustomerSearch(''); }}
                          className="w-full text-left p-2 text-sm hover:bg-slate-50 border-b">
                          {c.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="border-t pt-2 space-y-1">
                <Label className="text-xs text-slate-500">Alınan Ödeme</Label>
                <Input type="number" step="0.01" value={paidAmount}
                  onChange={(e) => setPaidAmount(e.target.value)}
                  className="h-8 text-sm text-right" placeholder="0"
                  data-testid="paid-amount-input" />
                {total > 0 && (
                  <div className="flex justify-between text-xs pt-1">
                    <span className="text-slate-500">Kalan Borç:</span>
                    <span className={`font-bold ${remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {remaining.toLocaleString('tr-TR')} TL
                    </span>
                  </div>
                )}
              </div>

              {/* Hesap Kategorisi seçici — müşterinin mevcut kategorileri varsa dropdown, yoksa serbest metin */}
              {selectedCustomer && (
                <div className="border-t pt-2 space-y-1">
                  <Label className="text-xs text-slate-500">
                    Hesap Kategorisi
                    <span className="text-slate-400 font-normal ml-1">(boş = Ana Hesap)</span>
                  </Label>
                  {customerAccounts.filter(a => a.label !== 'Ana Hesap').length > 0 ? (
                    <div className="space-y-1">
                      <select
                        value={accountLabel === '__new__' ? '__new__' : (accountLabel || '')}
                        onChange={(e) => setAccountLabel(e.target.value === '__new__' ? '__new__' : e.target.value)}
                        className="w-full h-8 rounded-md border border-input bg-white text-sm px-2"
                        data-testid="pos-account-select"
                      >
                        <option value="">Ana Hesap</option>
                        {customerAccounts.filter(a => a.label !== 'Ana Hesap').map(a => (
                          <option key={a.label} value={a.label}>
                            {a.label} · {a.balance > 0 ? `${a.balance.toLocaleString('tr-TR')} TL borç` : a.balance < 0 ? `${Math.abs(a.balance).toLocaleString('tr-TR')} TL alacak` : 'kapalı'}
                          </option>
                        ))}
                        <option value="__new__">➕ Yeni kategori...</option>
                      </select>
                      {accountLabel === '__new__' && (
                        <Input
                          autoFocus
                          placeholder="Yeni kategori adı (ör: İnşaat İşi)"
                          value=""
                          onChange={(e) => setAccountLabel(e.target.value)}
                          className="h-8 text-sm"
                          data-testid="pos-account-new-input"
                        />
                      )}
                    </div>
                  ) : (
                    <Input
                      placeholder="Ör: İnşaat İşi (boş bırakılırsa Ana Hesap'a eklenir)"
                      value={accountLabel === '__new__' ? '' : accountLabel}
                      onChange={(e) => setAccountLabel(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="pos-account-input"
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Input placeholder="Not..." value={note} onChange={(e) => setNote(e.target.value)} className="h-8 text-sm" />

        <Button onClick={completeSale} disabled={processing || cart.length === 0}
          className="h-14 text-lg bg-emerald-600 hover:bg-emerald-700" data-testid="complete-sale-btn">
          {processing ? 'Kaydediliyor...' : `Satışı Tamamla (${total.toLocaleString('tr-TR')} TL)`}
        </Button>
      </div>

      {/* New Product Dialog */}
      <Dialog open={showNewProductDialog} onOpenChange={setShowNewProductDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Ürün Bulunamadı</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">
            <strong>{newProductBarcode}</strong> barkoduna kayıtlı ürün yok. Yeni urun olarak eklemek ister misiniz?
          </p>
          <div className="space-y-2">
            <div className="space-y-1">
              <Label>Ürün Adı *</Label>
              <Input value={newProdForm.name} onChange={e => setNewProdForm({...newProdForm, name: e.target.value})}
                placeholder="Urun adi" data-testid="new-product-name" />
            </div>
            <div className="space-y-1">
              <Label>Satis Fiyati (TL)</Label>
              <Input type="number" step="0.01" value={newProdForm.sale_price}
                onChange={e => setNewProdForm({...newProdForm, sale_price: e.target.value})} placeholder="0" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowNewProductDialog(false)} className="flex-1">İptal</Button>
            <Button onClick={saveNewProduct} className="flex-1 bg-emerald-600 hover:bg-emerald-700">Ekle ve Sepete Koy</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* POS Satış Fişi Dialog */}
      <Dialog open={!!lastSale} onOpenChange={() => setLastSale(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Satış Tamamlandı</DialogTitle></DialogHeader>
          <div className="space-y-3 text-center">
            <div className="p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-slate-500">Toplam</p>
              <p className="text-2xl font-bold text-emerald-700">{Number(lastSale?.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</p>
              {lastSale?.is_veresiye && <p className="text-xs text-orange-600 mt-1">Veresiye</p>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLastSale(null)} className="flex-1">Kapat</Button>
              <Button onClick={() => { printReceipt(lastSale); }} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="print-pos-receipt">
                <Printer className="h-4 w-4 mr-1" />Fiş Yazdır
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
